<template>
    <div class="fixed left-0 right-0 top-10 z-30 flex flex-col items-center">
        <prime-message
            v-if="resetCounter !== null"
            severity="warn"
            :pt="{
                button: {
                    class: 'hidden'
                }
            }"
            @close="handleClose"
        >
            <div
                class="flex items-center justify-between gap-16 lg:min-w-[500px]"
            >
                <div>{{ resetMessage }}</div>

                <prime-button
                    :label="t('layout.stillWorking')"
                    @click="handleClose"
                />
            </div>
        </prime-message>
    </div>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button'
import PrimeMessage from 'primevue/message'
import { useIdle, useIntervalFn } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useSetCartData } from '~/composables/use-cart'
import {
    useLogoutMutation,
    useSetViewer,
    useViewer
} from '~/composables/use-login'
import { useQueryClient } from '@tanstack/vue-query'
import { useRouteLink } from '~/composables/routing'
import { useState } from '#app'

const props = defineProps<{
    idleTimeout: number
    idleTimeoutMessage: string
    facilitySlug?: string
}>()

const { t } = useI18n({ useScope: 'global' })
const { mutate: logout } = useLogoutMutation()
const queryClient = useQueryClient()
const router = useRouter()
const activeFacility = useActiveFacility()
const { facilityHomeLink } = useRouteLink()
const viewer = useViewer()
const setViewer = useSetViewer()
const setCartData = useSetCartData()

// Begin the warning ten seconds before the configured sign-out deadline.
const { idle, reset } = useIdle((props.idleTimeout - 10) * 1000)
const resetCounter = ref<number | null>(null)

// Agreement videos are active customer interaction even when no pointer events fire.
const isWatchingWistiaVideo = useState<boolean>(
    'isWatchingWistiaVideo',
    () => false
)
watch(isWatchingWistiaVideo, newValue => {
    if (!newValue) {
        reset()
    }
})

watch(idle, newValue => {
    if (
        newValue &&
        resetCounter.value === null &&
        !isWatchingWistiaVideo.value &&
        viewer.value !== undefined
    ) {
        resetCounter.value = 10
    }

    if (!newValue && resetCounter.value !== null) {
        resetCounter.value = null
    }
})

useIntervalFn(() => {
    if (resetCounter.value !== null) {
        resetCounter.value -= 1
        if (resetCounter.value < 1) {
            resetCounter.value = null
            signOut()
        }
    }
}, 1000)

const resetMessage = computed(() => {
    if (props.idleTimeoutMessage !== '') {
        return props.idleTimeoutMessage.replace(
            '{{counter}}',
            String(resetCounter.value)
        )
    }

    return t(
        'layout.resetPrompt',
        { count: resetCounter.value ?? 0 },
        resetCounter.value ?? 0
    )
})

function handleClose() {
    resetCounter.value = null
}

function signOut() {
    logout(undefined, {
        onSuccess: data => {
            if (data.logout === true) {
                setViewer(undefined)
                setCartData(undefined)
                queryClient.invalidateQueries()
                // Profile layouts use the customer's home facility, so retain
                // the kiosk's originating facility when it is supplied.
                router.push(
                    facilityHomeLink(
                        props.facilitySlug ?? activeFacility.value.slug
                    )
                )
            } else {
                throw new Error()
            }
        },
        onSettled() {
            queryClient.invalidateQueries()
        }
    })
}
</script>
